import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Edit,
  Plus,
  Trash2
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../../components/Modal";
import { Notification } from "../../../components/Notification";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import {
  ContenidoEducativo,
  CreateContenidoDto,
  CreateModuloDto,
  CreateMundoDto,
  CreatePreguntaDto,
  ejerciciosEducativosService,
  ModuloEducativo,
  MundoEducativo,
  PreguntaEjercicioEducativo
} from "../../../services/admin/ejercicios-educativos/ejercicios-educativos.service";
import { AdminLayout } from "../components";

const EjerciciosEducativosPage: React.FC = () => {
  const [mundos, setMundos] = useState<MundoEducativo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingModulos, setLoadingModulos] = useState<Set<number>>(new Set());
  const [selectedMundo, setSelectedMundo] = useState<MundoEducativo | null>(
    null
  );
  const [selectedModulo, setSelectedModulo] = useState<ModuloEducativo | null>(
    null
  );
  const [selectedContenido, setSelectedContenido] =
    useState<ContenidoEducativo | null>(null);
  const [expandedMundos, setExpandedMundos] = useState<Set<number>>(
    new Set()
  );
  const [expandedModulos, setExpandedModulos] = useState<Set<number>>(
    new Set()
  );
  const [expandedContenidos, setExpandedContenidos] = useState<Set<number>>(
    new Set()
  );
  // Cache de módulos cargados con contenidos
  const [modulosCache, setModulosCache] = useState<
    Map<number, ModuloEducativo>
  >(new Map());

  // Modales
  const [showMundoModal, setShowMundoModal] = useState(false);
  const [showModuloModal, setShowModuloModal] = useState(false);
  const [showContenidoModal, setShowContenidoModal] = useState(false);
  const [showPreguntaModal, setShowPreguntaModal] = useState(false);
  const [editingMundo, setEditingMundo] = useState<MundoEducativo | null>(null);
  const [editingModulo, setEditingModulo] = useState<ModuloEducativo | null>(null);
  const [editingContenido, setEditingContenido] = useState<ContenidoEducativo | null>(null);
  const [editingPregunta, setEditingPregunta] = useState<PreguntaEjercicioEducativo | null>(null);
  const [deleteMundoConfirm, setDeleteMundoConfirm] = useState<MundoEducativo | null>(null);
  const [deleteModuloConfirm, setDeleteModuloConfirm] = useState<ModuloEducativo | null>(null);
  const [deleteContenidoConfirm, setDeleteContenidoConfirm] = useState<ContenidoEducativo | null>(null);
  const [deletePreguntaConfirm, setDeletePreguntaConfirm] = useState<PreguntaEjercicioEducativo | null>(null);

  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  } | null>(null);

  // Formularios
  const [mundoForm, setMundoForm] = useState<CreateMundoDto>({
    nombre: "",
    descripcion: "",
    icono: "",
    orden: 1,
  });
  const [moduloForm, setModuloForm] = useState<CreateModuloDto>({
    mundoId: 0,
    nombre: "",
    descripcion: "",
    orden: 1,
  });
  const [contenidoForm, setContenidoForm] = useState<CreateContenidoDto>({
    moduloId: 0,
    tipoContenido: "CONCEPTOS",
    titulo: "",
    contenido: "",
    imagenUrl: "",
    orden: 1,
  });
  const [preguntaForm, setPreguntaForm] = useState<CreatePreguntaDto>({
    contenidoId: 0,
    tipoPregunta: "SELECCION_MULTIPLE",
    pregunta: "",
    opciones: [
      { valor: "a", texto: "", esCorrecta: false },
      { valor: "b", texto: "", esCorrecta: false },
      { valor: "c", texto: "", esCorrecta: false },
      { valor: "d", texto: "", esCorrecta: false },
    ],
    respuestaCorrecta: "",
    explicacion: "",
    imagenUrl: "",
    orden: 1,
  });

  const showNotification = useCallback(
    (type: "success" | "error", message: string) => {
      setNotification({
        isOpen: true,
        type,
        title: type === "success" ? "Éxito" : "Error",
        message,
      });
    },
    []
  );

  const loadMundos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ejerciciosEducativosService.getMundos();
      setMundos(data);
    } catch (error: any) {
      console.error("Error cargando mundos:", error);
      showNotification(
        "error",
        error.response?.data?.error || "Error cargando mundos"
      );
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadMundos();
  }, [loadMundos]);

  const toggleMundo = (mundoId: number) => {
    const newExpanded = new Set(expandedMundos);
    if (newExpanded.has(mundoId)) {
      newExpanded.delete(mundoId);
    } else {
      newExpanded.add(mundoId);
    }
    setExpandedMundos(newExpanded);
  };

  const loadModuloContenidos = async (moduloId: number) => {
    try {
      setLoadingModulos((prev) => new Set(prev).add(moduloId));
      const moduloCompleto = await ejerciciosEducativosService.getModuloById(
        moduloId
      );
      setModulosCache((prev) => {
        const newCache = new Map(prev);
        newCache.set(moduloId, moduloCompleto);
        return newCache;
      });
      // Actualizar el módulo en la lista de mundos
      setMundos((prevMundos) =>
        prevMundos.map((mundo) => ({
          ...mundo,
          modulos: mundo.modulos?.map((mod) =>
            mod.idModuloEducativo === moduloId ? moduloCompleto : mod
          ),
        }))
      );
    } catch (error: any) {
      console.error("Error cargando módulo:", error);
      showNotification(
        "error",
        error.response?.data?.error || "Error cargando módulo"
      );
    } finally {
      setLoadingModulos((prev) => {
        const newSet = new Set(prev);
        newSet.delete(moduloId);
        return newSet;
      });
    }
  };

  const loadContenidoPreguntas = async (contenidoId: number) => {
    try {
      const contenidoCompleto = await ejerciciosEducativosService.getContenidoById(
        contenidoId
      );
      // Actualizar el contenido en la lista de mundos
      setMundos((prevMundos) =>
        prevMundos.map((mundo) => ({
          ...mundo,
          modulos: mundo.modulos?.map((modulo) => ({
            ...modulo,
            contenidos: modulo.contenidos?.map((cont) =>
              cont.idContenidoEducativo === contenidoId ? contenidoCompleto : cont
            ),
          })),
        }))
      );
    } catch (error: any) {
      console.error("Error cargando contenido:", error);
      showNotification(
        "error",
        error.response?.data?.error || "Error cargando contenido"
      );
    }
  };

  const toggleModulo = async (moduloId: number) => {
    const newExpanded = new Set(expandedModulos);
    if (newExpanded.has(moduloId)) {
      newExpanded.delete(moduloId);
    } else {
      newExpanded.add(moduloId);
      // Cargar contenidos del módulo si no están en cache
      if (!modulosCache.has(moduloId)) {
        await loadModuloContenidos(moduloId);
      }
    }
    setExpandedModulos(newExpanded);
  };

  const toggleContenido = async (contenidoId: number) => {
    const newExpanded = new Set(expandedContenidos);
    if (newExpanded.has(contenidoId)) {
      newExpanded.delete(contenidoId);
    } else {
      newExpanded.add(contenidoId);
      // Cargar preguntas del contenido si no están cargadas
      const contenido = mundos
        .flatMap(m => m.modulos || [])
        .flatMap(mod => mod.contenidos || [])
        .find(cont => cont.idContenidoEducativo === contenidoId);
      // Si el contenido no tiene preguntas cargadas o el array está vacío, recargarlo
      if (!contenido?.preguntas || contenido.preguntas.length === 0) {
        await loadContenidoPreguntas(contenidoId);
      }
    }
    setExpandedContenidos(newExpanded);
  };

  const handleCreateMundo = async () => {
    try {
      if (!mundoForm.nombre.trim()) {
        showNotification("error", "El nombre es requerido");
        return;
      }
      await ejerciciosEducativosService.createMundo(mundoForm);
      showNotification("success", "Mundo creado exitosamente");
      setShowMundoModal(false);
      setEditingMundo(null);
      setMundoForm({ nombre: "", descripcion: "", icono: "", orden: 1 });
      loadMundos();
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al crear mundo"
      );
    }
  };

  const handleUpdateMundo = async () => {
    if (!editingMundo) return;
    try {
      if (!mundoForm.nombre.trim()) {
        showNotification("error", "El nombre es requerido");
        return;
      }
      await ejerciciosEducativosService.updateMundo(editingMundo.idMundoEducativo, mundoForm);
      showNotification("success", "Mundo actualizado exitosamente");
      setShowMundoModal(false);
      setEditingMundo(null);
      setMundoForm({ nombre: "", descripcion: "", icono: "", orden: 1 });
      loadMundos();
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al actualizar mundo"
      );
    }
  };

  const handleDeleteMundo = async () => {
    if (!deleteMundoConfirm) return;
    try {
      await ejerciciosEducativosService.deleteMundo(deleteMundoConfirm.idMundoEducativo);
      showNotification("success", "Mundo eliminado exitosamente");
      setDeleteMundoConfirm(null);
      loadMundos();
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al eliminar mundo"
      );
    }
  };

  const openEditMundoModal = (mundo: MundoEducativo) => {
    setEditingMundo(mundo);
    setMundoForm({
      nombre: mundo.nombre,
      descripcion: mundo.descripcion || "",
      icono: mundo.icono || "",
      orden: mundo.orden,
    });
    setShowMundoModal(true);
  };

  const handleCreateModulo = async () => {
    try {
      if (!moduloForm.nombre.trim() || !moduloForm.mundoId) {
        showNotification("error", "El nombre y mundo son requeridos");
        return;
      }
      await ejerciciosEducativosService.createModulo(moduloForm);
      showNotification("success", "Módulo creado exitosamente");
      setShowModuloModal(false);
      setEditingModulo(null);
      setModuloForm({
        mundoId: 0,
        nombre: "",
        descripcion: "",
        orden: 1,
      });
      loadMundos();
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al crear módulo"
      );
    }
  };

  const handleUpdateModulo = async () => {
    if (!editingModulo) return;
    try {
      if (!moduloForm.nombre.trim()) {
        showNotification("error", "El nombre es requerido");
        return;
      }
      await ejerciciosEducativosService.updateModulo(editingModulo.idModuloEducativo, moduloForm);
      showNotification("success", "Módulo actualizado exitosamente");
      setShowModuloModal(false);
      setEditingModulo(null);
      setModuloForm({
        mundoId: 0,
        nombre: "",
        descripcion: "",
        orden: 1,
      });
      loadMundos();
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al actualizar módulo"
      );
    }
  };

  const handleDeleteModulo = async () => {
    if (!deleteModuloConfirm) return;
    try {
      await ejerciciosEducativosService.deleteModulo(deleteModuloConfirm.idModuloEducativo);
      showNotification("success", "Módulo eliminado exitosamente");
      setDeleteModuloConfirm(null);
      loadMundos();
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al eliminar módulo"
      );
    }
  };

  const openEditModuloModal = (modulo: ModuloEducativo) => {
    setEditingModulo(modulo);
    setModuloForm({
      mundoId: modulo.mundoId,
      nombre: modulo.nombre,
      descripcion: modulo.descripcion || "",
      orden: modulo.orden,
    });
    setSelectedMundo(mundos.find(m => m.idMundoEducativo === modulo.mundoId) || null);
    setShowModuloModal(true);
  };

  const handleCreateContenido = async () => {
    try {
      if (
        !contenidoForm.contenido.trim() ||
        !contenidoForm.moduloId ||
        !contenidoForm.tipoContenido
      ) {
        showNotification("error", "Los campos requeridos deben estar completos");
        return;
      }
      await ejerciciosEducativosService.createContenido(contenidoForm);
      showNotification("success", "Contenido creado exitosamente");
      setShowContenidoModal(false);
      setEditingContenido(null);
      const moduloIdGuardado = contenidoForm.moduloId;
      setContenidoForm({
        moduloId: 0,
        tipoContenido: "CONCEPTOS",
        titulo: "",
        contenido: "",
        imagenUrl: "",
        orden: 1,
      });
      // Asegurar que el módulo esté expandido
      if (moduloIdGuardado) {
        const newExpanded = new Set(expandedModulos);
        newExpanded.add(moduloIdGuardado);
        setExpandedModulos(newExpanded);
        // Recargar el módulo específico para actualizar los contenidos
        await loadModuloContenidos(moduloIdGuardado);
      }
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al crear contenido"
      );
    }
  };

  const handleUpdateContenido = async () => {
    if (!editingContenido) return;
    try {
      if (!contenidoForm.contenido.trim()) {
        showNotification("error", "El contenido es requerido");
        return;
      }
      const moduloIdGuardado = editingContenido.moduloId;
      await ejerciciosEducativosService.updateContenido(editingContenido.idContenidoEducativo, contenidoForm);
      showNotification("success", "Contenido actualizado exitosamente");
      setShowContenidoModal(false);
      setEditingContenido(null);
      setContenidoForm({
        moduloId: 0,
        tipoContenido: "CONCEPTOS",
        titulo: "",
        contenido: "",
        imagenUrl: "",
        orden: 1,
      });
      // Asegurar que el módulo esté expandido y recargar sus contenidos
      if (moduloIdGuardado) {
        const newExpanded = new Set(expandedModulos);
        newExpanded.add(moduloIdGuardado);
        setExpandedModulos(newExpanded);
        // Recargar el módulo específico para actualizar los contenidos
        await loadModuloContenidos(moduloIdGuardado);
      }
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al actualizar contenido"
      );
    }
  };

  const handleDeleteContenido = async () => {
    if (!deleteContenidoConfirm) return;
    try {
      const moduloIdGuardado = deleteContenidoConfirm.moduloId;
      await ejerciciosEducativosService.deleteContenido(deleteContenidoConfirm.idContenidoEducativo);
      showNotification("success", "Contenido eliminado exitosamente");
      setDeleteContenidoConfirm(null);
      // Asegurar que el módulo esté expandido y recargar sus contenidos
      if (moduloIdGuardado) {
        const newExpanded = new Set(expandedModulos);
        newExpanded.add(moduloIdGuardado);
        setExpandedModulos(newExpanded);
        // Recargar el módulo específico para actualizar los contenidos
        await loadModuloContenidos(moduloIdGuardado);
      }
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al eliminar contenido"
      );
    }
  };

  const openEditContenidoModal = (contenido: ContenidoEducativo) => {
    setEditingContenido(contenido);
    setContenidoForm({
      moduloId: contenido.moduloId,
      tipoContenido: contenido.tipoContenido,
      titulo: contenido.titulo || "",
      contenido: contenido.contenido,
      imagenUrl: contenido.imagenUrl || "",
      orden: contenido.orden,
    });
    setSelectedModulo(mundos
      .flatMap(m => m.modulos || [])
      .find(m => m.idModuloEducativo === contenido.moduloId) || null);
    setShowContenidoModal(true);
  };

  const handleCreatePregunta = async () => {
    try {
      if (
        !preguntaForm.pregunta.trim() ||
        !preguntaForm.contenidoId ||
        !preguntaForm.respuestaCorrecta ||
        preguntaForm.opciones.some((op) => !op.texto.trim())
      ) {
        showNotification(
          "error",
          "Todos los campos requeridos deben estar completos"
        );
        return;
      }
      // Validar que haya al menos una opción correcta
      const tieneCorrecta = preguntaForm.opciones.some(
        (op) => op.esCorrecta
      );
      if (!tieneCorrecta) {
        showNotification("error", "Debe haber al menos una opción correcta");
        return;
      }
      const contenidoIdGuardado = preguntaForm.contenidoId;
      await ejerciciosEducativosService.createPregunta(preguntaForm);
      showNotification("success", "Pregunta creada exitosamente");
      setShowPreguntaModal(false);
      setPreguntaForm({
        contenidoId: 0,
        tipoPregunta: "SELECCION_MULTIPLE",
        pregunta: "",
        opciones: [
          { valor: "a", texto: "", esCorrecta: false },
          { valor: "b", texto: "", esCorrecta: false },
          { valor: "c", texto: "", esCorrecta: false },
          { valor: "d", texto: "", esCorrecta: false },
        ],
        respuestaCorrecta: "",
        explicacion: "",
        imagenUrl: "",
        orden: 1,
      });
      // Asegurar que el contenido esté expandido y recargar sus preguntas
      if (contenidoIdGuardado) {
        const newExpanded = new Set(expandedContenidos);
        newExpanded.add(contenidoIdGuardado);
        setExpandedContenidos(newExpanded);
        // Recargar el contenido específico para actualizar las preguntas
        await loadContenidoPreguntas(contenidoIdGuardado);
      }
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al crear pregunta"
      );
    }
  };

  const handleUpdatePregunta = async () => {
    if (!editingPregunta) return;
    try {
      if (
        !preguntaForm.pregunta.trim() ||
        !preguntaForm.contenidoId ||
        !preguntaForm.respuestaCorrecta ||
        preguntaForm.opciones.some((op) => !op.texto.trim())
      ) {
        showNotification(
          "error",
          "Todos los campos requeridos deben estar completos"
        );
        return;
      }
      // Validar que haya al menos una opción correcta
      const tieneCorrecta = preguntaForm.opciones.some(
        (op) => op.esCorrecta
      );
      if (!tieneCorrecta) {
        showNotification("error", "Debe haber al menos una opción correcta");
        return;
      }
      const contenidoIdGuardado = preguntaForm.contenidoId;
      await ejerciciosEducativosService.updatePregunta(editingPregunta.idPreguntaEjercicioEducativo, preguntaForm);
      showNotification("success", "Pregunta actualizada exitosamente");
      setShowPreguntaModal(false);
      setEditingPregunta(null);
      setPreguntaForm({
        contenidoId: 0,
        tipoPregunta: "SELECCION_MULTIPLE",
        pregunta: "",
        opciones: [
          { valor: "a", texto: "", esCorrecta: false },
          { valor: "b", texto: "", esCorrecta: false },
          { valor: "c", texto: "", esCorrecta: false },
          { valor: "d", texto: "", esCorrecta: false },
        ],
        respuestaCorrecta: "",
        explicacion: "",
        imagenUrl: "",
        orden: 1,
      });
      // Asegurar que el contenido esté expandido y recargar sus preguntas
      if (contenidoIdGuardado) {
        const newExpanded = new Set(expandedContenidos);
        newExpanded.add(contenidoIdGuardado);
        setExpandedContenidos(newExpanded);
        // Recargar el contenido específico para actualizar las preguntas
        await loadContenidoPreguntas(contenidoIdGuardado);
      }
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al actualizar pregunta"
      );
    }
  };

  const handleDeletePregunta = async () => {
    if (!deletePreguntaConfirm) return;
    try {
      const contenidoIdGuardado = deletePreguntaConfirm.contenidoId;
      await ejerciciosEducativosService.deletePregunta(deletePreguntaConfirm.idPreguntaEjercicioEducativo);
      showNotification("success", "Pregunta eliminada exitosamente");
      setDeletePreguntaConfirm(null);
      // Asegurar que el contenido esté expandido y recargar sus preguntas
      if (contenidoIdGuardado) {
        const newExpanded = new Set(expandedContenidos);
        newExpanded.add(contenidoIdGuardado);
        setExpandedContenidos(newExpanded);
        // Recargar el contenido específico para actualizar las preguntas
        await loadContenidoPreguntas(contenidoIdGuardado);
      }
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.error || "Error al eliminar pregunta"
      );
    }
  };

  const openModuloModal = (mundo: MundoEducativo) => {
    setSelectedMundo(mundo);
    setModuloForm({ ...moduloForm, mundoId: mundo.idMundoEducativo });
    setShowModuloModal(true);
  };

  const openContenidoModal = (modulo: ModuloEducativo) => {
    setSelectedModulo(modulo);
    setContenidoForm({ ...contenidoForm, moduloId: modulo.idModuloEducativo });
    setShowContenidoModal(true);
  };

  const openPreguntaModal = (contenido: ContenidoEducativo) => {
    setSelectedContenido(contenido);
    setEditingPregunta(null);
    setPreguntaForm({
      contenidoId: contenido.idContenidoEducativo,
      tipoPregunta: "SELECCION_MULTIPLE",
      pregunta: "",
      opciones: [
        { valor: "a", texto: "", esCorrecta: false },
        { valor: "b", texto: "", esCorrecta: false },
        { valor: "c", texto: "", esCorrecta: false },
        { valor: "d", texto: "", esCorrecta: false },
      ],
      respuestaCorrecta: "",
      explicacion: "",
      imagenUrl: "",
      orden: 1,
    });
    setShowPreguntaModal(true);
  };

  const openEditPreguntaModal = (pregunta: PreguntaEjercicioEducativo) => {
    setEditingPregunta(pregunta);
    // Encontrar el contenido al que pertenece la pregunta
    const contenido = mundos
      .flatMap(m => m.modulos || [])
      .flatMap(mod => mod.contenidos || [])
      .find(cont => cont.idContenidoEducativo === pregunta.contenidoId);
    setSelectedContenido(contenido || null);
    setPreguntaForm({
      contenidoId: pregunta.contenidoId,
      tipoPregunta: pregunta.tipoPregunta,
      pregunta: pregunta.pregunta,
      opciones: pregunta.opciones.map(op => ({ ...op })),
      respuestaCorrecta: pregunta.respuestaCorrecta,
      explicacion: pregunta.explicacion || "",
      imagenUrl: pregunta.imagenUrl || "",
      orden: pregunta.orden,
    });
    setShowPreguntaModal(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Función helper para obtener color según tipo de contenido
  const getTipoContenidoColor = (tipo: string) => {
    switch (tipo) {
      case "CONCEPTOS":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "FORMULAS":
        return "bg-purple-100 text-purple-700 border-purple-200";
      case "EJEMPLOS":
        return "bg-green-100 text-green-700 border-green-200";
      case "EJERCICIOS":
        return "bg-orange-100 text-orange-700 border-orange-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  // Función helper para obtener icono según tipo de contenido
  const getTipoContenidoIcon = (tipo: string) => {
    switch (tipo) {
      case "CONCEPTOS":
        return "📚";
      case "FORMULAS":
        return "📐";
      case "EJEMPLOS":
        return "💡";
      case "EJERCICIOS":
        return "✏️";
      default:
        return "📄";
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {/* Header mejorado */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Ejercicios Educativos
              </h1>
              <p className="text-gray-600 text-lg">
                Gestiona mundos, módulos, contenidos y preguntas
              </p>
            </div>
            <Button 
              onClick={() => setShowMundoModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6 py-3 text-base font-semibold"
            >
              <Plus className="h-5 w-5 mr-2" />
              Crear Mundo
            </Button>
          </div>
        </div>

        {/* Árbol de estructura mejorado */}
        <Card className="shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
            <CardTitle className="flex items-center gap-3 text-2xl font-bold text-gray-800">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <BookOpen className="h-6 w-6 text-blue-600" />
              </div>
              Estructura de Ejercicios
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {mundos.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-4">
                  <BookOpen className="h-10 w-10 text-blue-600" />
                </div>
                <p className="text-gray-600 text-xl font-medium mb-2">
                  No hay mundos creados todavía.
                </p>
                <p className="text-gray-400 text-sm">
                  Crea tu primer mundo para comenzar.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {mundos.map((mundo) => (
                  <div key={mundo.idMundoEducativo} className="bg-white border-2 border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden">
                    {/* Mundo mejorado */}
                    <div
                      className="flex items-center justify-between p-5 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 cursor-pointer transition-all duration-200"
                      onClick={() => toggleMundo(mundo.idMundoEducativo)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm">
                          {expandedMundos.has(mundo.idMundoEducativo) ? (
                            <ChevronDown className="h-5 w-5 text-blue-600 font-bold" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-blue-600 font-bold" />
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="font-bold text-xl text-gray-800 block">
                            {mundo.nombre}
                          </span>
                          {mundo.descripcion && (
                            <span className="text-sm text-gray-600 mt-1 block">
                              {mundo.descripcion}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditMundoModal(mundo);
                          }}
                          className="bg-white hover:bg-blue-50 border-blue-200 text-blue-700 hover:text-blue-800 shadow-sm hover:shadow transition-all"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteMundoConfirm(mundo);
                          }}
                          className="bg-white text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 shadow-sm hover:shadow transition-all"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Eliminar
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModuloModal(mundo);
                          }}
                          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg transition-all"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Módulo
                        </Button>
                      </div>
                    </div>

                    {/* Módulos mejorados */}
                    {expandedMundos.has(mundo.idMundoEducativo) &&
                      mundo.modulos &&
                      mundo.modulos.length > 0 && (
                        <div className="pl-6 pr-4 py-3 bg-gray-50 border-t border-gray-200">
                          {mundo.modulos.map((modulo) => (
                            <div
                              key={modulo.idModuloEducativo}
                              className="bg-white rounded-lg shadow-sm border border-gray-200 mb-2 last:mb-0 overflow-hidden hover:shadow-md transition-all duration-200"
                            >
                              <div
                                className="flex items-center justify-between p-4 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer transition-all duration-200"
                                onClick={() =>
                                  toggleModulo(modulo.idModuloEducativo)
                                }
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="flex items-center justify-center w-7 h-7 rounded-md bg-indigo-100">
                                    {expandedModulos.has(
                                      modulo.idModuloEducativo
                                    ) ? (
                                      <ChevronDown className="h-4 w-4 text-indigo-600" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-indigo-600" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <span className="font-semibold text-base text-gray-800 block">
                                      {modulo.nombre}
                                    </span>
                                    {modulo.descripcion && (
                                      <span className="text-xs text-gray-500 mt-1 block line-clamp-1">
                                        {modulo.descripcion}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditModuloModal(modulo);
                                    }}
                                    className="bg-white hover:bg-indigo-50 border-indigo-200 text-indigo-700 hover:text-indigo-800 shadow-sm hover:shadow transition-all text-xs px-2 py-1"
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteModuloConfirm(modulo);
                                    }}
                                    className="bg-white text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 shadow-sm hover:shadow transition-all text-xs px-2 py-1"
                                  >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Eliminar
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openContenidoModal(modulo);
                                    }}
                                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-sm hover:shadow-md transition-all text-xs px-2 py-1"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Contenido
                                  </Button>
                                </div>
                              </div>

                              {/* Contenidos mejorados */}
                              {expandedModulos.has(
                                modulo.idModuloEducativo
                              ) && (
                                <>
                                  {loadingModulos.has(
                                    modulo.idModuloEducativo
                                  ) ? (
                                    <div className="pl-6 pr-4 py-3 bg-gray-50">
                                      <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                                        Cargando contenidos...
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {modulo.contenidos &&
                                      modulo.contenidos.length > 0 ? (
                                        <div className="pl-6 pr-4 py-2 bg-gray-50 space-y-2">
                                          {modulo.contenidos.map((contenido) => (
                                            <div
                                              key={contenido.idContenidoEducativo}
                                              className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                                            >
                                              <div
                                                className="flex items-center justify-between p-3 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 cursor-pointer transition-all duration-200"
                                                onClick={() =>
                                                  toggleContenido(
                                                    contenido.idContenidoEducativo
                                                  )
                                                }
                                              >
                                                <div className="flex items-center gap-3 flex-1">
                                                  <div className={`flex items-center justify-center w-6 h-6 rounded-md ${getTipoContenidoColor(contenido.tipoContenido)}`}>
                                                    {expandedContenidos.has(
                                                      contenido.idContenidoEducativo
                                                    ) ? (
                                                      <ChevronDown className="h-3 w-3" />
                                                    ) : (
                                                      <ChevronRight className="h-3 w-3" />
                                                    )}
                                                  </div>
                                                  <span className="text-lg mr-2">{getTipoContenidoIcon(contenido.tipoContenido)}</span>
                                                  <div className="flex items-center gap-2 flex-1">
                                                    <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${getTipoContenidoColor(contenido.tipoContenido)}`}>
                                                      {contenido.tipoContenido}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-700">
                                                      {contenido.titulo ||
                                                        contenido.tipoContenido}
                                                    </span>
                                                  </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      openEditContenidoModal(contenido);
                                                    }}
                                                    className="bg-white hover:bg-purple-50 border-purple-200 text-purple-700 hover:text-purple-800 shadow-sm hover:shadow transition-all text-xs px-2 py-1 h-7"
                                                  >
                                                    <Edit className="h-3 w-3 mr-1" />
                                                    Editar
                                                  </Button>
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setDeleteContenidoConfirm(contenido);
                                                    }}
                                                    className="bg-white text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 shadow-sm hover:shadow transition-all text-xs px-2 py-1 h-7"
                                                  >
                                                    <Trash2 className="h-3 w-3 mr-1" />
                                                    Eliminar
                                                  </Button>
                                                  {contenido.tipoContenido ===
                                                    "EJERCICIOS" && (
                                                    <Button
                                                      size="sm"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        openPreguntaModal(contenido);
                                                      }}
                                                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-sm hover:shadow-md transition-all text-xs px-2 py-1 h-7"
                                                    >
                                                      <Plus className="h-3 w-3 mr-1" />
                                                      Pregunta
                                                    </Button>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Preguntas mejoradas */}
                                              {expandedContenidos.has(
                                                contenido.idContenidoEducativo
                                              ) &&
                                                contenido.preguntas &&
                                                contenido.preguntas.length > 0 && (
                                                  <div className="pl-6 pr-4 py-2 bg-gradient-to-br from-gray-50 to-white border-t border-gray-200">
                                                    <div className="space-y-1">
                                                      {contenido.preguntas.map(
                                                        (pregunta) => (
                                                          <div
                                                            key={
                                                              pregunta.idPreguntaEjercicioEducativo
                                                            }
                                                            className="group bg-white rounded-lg border border-gray-200 p-3 flex items-center justify-between hover:border-orange-300 hover:shadow-md transition-all duration-200"
                                                          >
                                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                                                                {pregunta.orden}
                                                              </div>
                                                              <div className="flex-1 min-w-0">
                                                                <p className="text-sm text-gray-800 font-medium leading-relaxed break-words">
                                                                  {pregunta.pregunta}
                                                                </p>
                                                                <span className="inline-block mt-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                                                  {pregunta.tipoPregunta}
                                                                </span>
                                                              </div>
                                                            </div>
                                                            <div className="flex gap-1 ml-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                              <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  openEditPreguntaModal(pregunta);
                                                                }}
                                                                className="h-7 w-7 p-0 bg-white hover:bg-orange-50 border-orange-200 text-orange-600 hover:text-orange-700 shadow-sm hover:shadow transition-all"
                                                              >
                                                                <Edit className="h-3.5 w-3.5" />
                                                              </Button>
                                                              <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  setDeletePreguntaConfirm(pregunta);
                                                                }}
                                                                className="h-7 w-7 p-0 bg-white text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 shadow-sm hover:shadow transition-all"
                                                              >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                              </Button>
                                                            </div>
                                                          </div>
                                                        )
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="pl-6 pr-4 py-4 bg-gray-50">
                                          <div className="text-center py-4">
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-200 mb-2">
                                              <BookOpen className="h-6 w-6 text-gray-400" />
                                            </div>
                                            <p className="text-sm text-gray-500 font-medium">
                                              No hay contenidos en este módulo.
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal Crear Mundo */}
        {showMundoModal && (
          <Modal
            isOpen={showMundoModal}
            onClose={() => {
              setShowMundoModal(false);
              setMundoForm({
                nombre: "",
                descripcion: "",
                icono: "",
                orden: 1,
              });
            }}
            title={editingMundo ? "Editar Mundo" : "Crear Mundo"}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="mundo-nombre">Nombre *</Label>
                <Input
                  id="mundo-nombre"
                  value={mundoForm.nombre}
                  onChange={(e) =>
                    setMundoForm({ ...mundoForm, nombre: e.target.value })
                  }
                  placeholder="Ej: Mundo 1 ¿Qué son los números?"
                />
              </div>
              <div>
                <Label htmlFor="mundo-descripcion">Descripción</Label>
                <Textarea
                  id="mundo-descripcion"
                  value={mundoForm.descripcion}
                  onChange={(e) =>
                    setMundoForm({
                      ...mundoForm,
                      descripcion: e.target.value,
                    })
                  }
                  placeholder="Descripción del mundo"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="mundo-icono">Icono (URL)</Label>
                <Input
                  id="mundo-icono"
                  value={mundoForm.icono}
                  onChange={(e) =>
                    setMundoForm({ ...mundoForm, icono: e.target.value })
                  }
                  placeholder="URL del icono"
                />
              </div>
              <div>
                <Label htmlFor="mundo-orden">Orden</Label>
                <Input
                  id="mundo-orden"
                  type="number"
                  value={mundoForm.orden}
                  onChange={(e) =>
                    setMundoForm({
                      ...mundoForm,
                      orden: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowMundoModal(false);
                    setEditingMundo(null);
                    setMundoForm({
                      nombre: "",
                      descripcion: "",
                      icono: "",
                      orden: 1,
                    });
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={editingMundo ? handleUpdateMundo : handleCreateMundo}>
                  {editingMundo ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Crear Módulo */}
        {showModuloModal && (
          <Modal
            isOpen={showModuloModal}
            onClose={() => {
              setShowModuloModal(false);
              setEditingModulo(null);
              setModuloForm({
                mundoId: 0,
                nombre: "",
                descripcion: "",
                orden: 1,
              });
            }}
            title={editingModulo ? `Editar Módulo - ${selectedMundo?.nombre || ""}` : `Crear Módulo - ${selectedMundo?.nombre || ""}`}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="modulo-nombre">Nombre *</Label>
                <Input
                  id="modulo-nombre"
                  value={moduloForm.nombre}
                  onChange={(e) =>
                    setModuloForm({ ...moduloForm, nombre: e.target.value })
                  }
                  placeholder="Ej: Módulo 1 - Conceptos"
                />
              </div>
              <div>
                <Label htmlFor="modulo-descripcion">Descripción</Label>
                <Textarea
                  id="modulo-descripcion"
                  value={moduloForm.descripcion}
                  onChange={(e) =>
                    setModuloForm({
                      ...moduloForm,
                      descripcion: e.target.value,
                    })
                  }
                  placeholder="Descripción del módulo"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="modulo-orden">Orden</Label>
                <Input
                  id="modulo-orden"
                  type="number"
                  value={moduloForm.orden}
                  onChange={(e) =>
                    setModuloForm({
                      ...moduloForm,
                      orden: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowModuloModal(false);
                    setEditingModulo(null);
                    setModuloForm({
                      mundoId: 0,
                      nombre: "",
                      descripcion: "",
                      orden: 1,
                    });
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={editingModulo ? handleUpdateModulo : handleCreateModulo}>
                  {editingModulo ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Crear Contenido */}
        {showContenidoModal && (
          <Modal
            isOpen={showContenidoModal}
            onClose={() => {
              setShowContenidoModal(false);
              setContenidoForm({
                moduloId: 0,
                tipoContenido: "CONCEPTOS",
                titulo: "",
                contenido: "",
                imagenUrl: "",
                orden: 1,
              });
            }}
            title={editingContenido ? `Editar Contenido - ${selectedModulo?.nombre || ""}` : `Crear Contenido - ${selectedModulo?.nombre || ""}`}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="contenido-tipo">Tipo de Contenido *</Label>
                <Select
                  value={contenidoForm.tipoContenido}
                  onValueChange={(value: any) =>
                    setContenidoForm({
                      ...contenidoForm,
                      tipoContenido: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CONCEPTOS">Conceptos</SelectItem>
                    <SelectItem value="FORMULAS">Fórmulas</SelectItem>
                    <SelectItem value="EJEMPLOS">Ejemplos</SelectItem>
                    <SelectItem value="EJERCICIOS">Ejercicios</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contenido-titulo">Título</Label>
                <Input
                  id="contenido-titulo"
                  value={contenidoForm.titulo}
                  onChange={(e) =>
                    setContenidoForm({
                      ...contenidoForm,
                      titulo: e.target.value,
                    })
                  }
                  placeholder="Ej: Ejercicios 1"
                />
              </div>
              <div>
                <Label htmlFor="contenido-contenido">Contenido *</Label>
                <Textarea
                  id="contenido-contenido"
                  value={contenidoForm.contenido}
                  onChange={(e) =>
                    setContenidoForm({
                      ...contenidoForm,
                      contenido: e.target.value,
                    })
                  }
                  placeholder="Contenido del texto..."
                  rows={8}
                />
              </div>
              <div>
                <Label htmlFor="contenido-imagen">Imagen (URL)</Label>
                <Input
                  id="contenido-imagen"
                  value={contenidoForm.imagenUrl}
                  onChange={(e) =>
                    setContenidoForm({
                      ...contenidoForm,
                      imagenUrl: e.target.value,
                    })
                  }
                  placeholder="URL de la imagen"
                />
              </div>
              <div>
                <Label htmlFor="contenido-orden">Orden</Label>
                <Input
                  id="contenido-orden"
                  type="number"
                  value={contenidoForm.orden}
                  onChange={(e) =>
                    setContenidoForm({
                      ...contenidoForm,
                      orden: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowContenidoModal(false);
                    setContenidoForm({
                      moduloId: 0,
                      tipoContenido: "CONCEPTOS",
                      titulo: "",
                      contenido: "",
                      imagenUrl: "",
                      orden: 1,
                    });
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={editingContenido ? handleUpdateContenido : handleCreateContenido}>
                  {editingContenido ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Crear/Editar Pregunta */}
        {showPreguntaModal && (
          <Modal
            isOpen={showPreguntaModal}
            onClose={() => {
              setShowPreguntaModal(false);
              setEditingPregunta(null);
              setPreguntaForm({
                contenidoId: 0,
                tipoPregunta: "SELECCION_MULTIPLE",
                pregunta: "",
                opciones: [
                  { valor: "a", texto: "", esCorrecta: false },
                  { valor: "b", texto: "", esCorrecta: false },
                  { valor: "c", texto: "", esCorrecta: false },
                  { valor: "d", texto: "", esCorrecta: false },
                ],
                respuestaCorrecta: "",
                explicacion: "",
                imagenUrl: "",
                orden: 1,
              });
            }}
            title={editingPregunta ? `Editar Pregunta - ${selectedContenido?.titulo || selectedContenido?.tipoContenido || ""}` : `Crear Pregunta - ${selectedContenido?.titulo || selectedContenido?.tipoContenido || ""}`}
          >
            <div className="space-y-4 max-h-[80vh] overflow-y-auto">
              <div>
                <Label htmlFor="pregunta-tipo">Tipo de Pregunta *</Label>
                <Select
                  value={preguntaForm.tipoPregunta}
                  onValueChange={(value: any) =>
                    setPreguntaForm({
                      ...preguntaForm,
                      tipoPregunta: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SELECCION_MULTIPLE">
                      Selección Múltiple
                    </SelectItem>
                    <SelectItem value="ASOCIACION">Asociación</SelectItem>
                    <SelectItem value="COMPLETAR">Completar</SelectItem>
                    <SelectItem value="ORDENAR">Ordenar</SelectItem>
                    <SelectItem value="UBICAR_RECTA">
                      Ubicar en Recta Numérica
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pregunta-texto">Pregunta *</Label>
                <Textarea
                  id="pregunta-texto"
                  value={preguntaForm.pregunta}
                  onChange={(e) =>
                    setPreguntaForm({
                      ...preguntaForm,
                      pregunta: e.target.value,
                    })
                  }
                  placeholder="Texto de la pregunta..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Opciones *</Label>
                <div className="space-y-2 mt-2">
                  {preguntaForm.opciones.map((opcion, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="font-medium w-6">{opcion.valor})</span>
                      <Input
                        value={opcion.texto}
                        onChange={(e) => {
                          const nuevasOpciones = [...preguntaForm.opciones];
                          nuevasOpciones[index].texto = e.target.value;
                          setPreguntaForm({
                            ...preguntaForm,
                            opciones: nuevasOpciones,
                          });
                        }}
                        placeholder={`Opción ${opcion.valor}`}
                        className="flex-1"
                      />
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={opcion.esCorrecta}
                          onChange={(e) => {
                            const nuevasOpciones = [...preguntaForm.opciones];
                            nuevasOpciones[index].esCorrecta = e.target.checked;
                            // Si esta es correcta, marcar como respuesta correcta
                            if (e.target.checked) {
                              setPreguntaForm({
                                ...preguntaForm,
                                opciones: nuevasOpciones,
                                respuestaCorrecta: opcion.valor,
                              });
                            } else {
                              setPreguntaForm({
                                ...preguntaForm,
                                opciones: nuevasOpciones,
                              });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Correcta</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="pregunta-explicacion">Explicación</Label>
                <Textarea
                  id="pregunta-explicacion"
                  value={preguntaForm.explicacion}
                  onChange={(e) =>
                    setPreguntaForm({
                      ...preguntaForm,
                      explicacion: e.target.value,
                    })
                  }
                  placeholder="Explicación de la respuesta correcta..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="pregunta-imagen">Imagen (URL)</Label>
                <Input
                  id="pregunta-imagen"
                  value={preguntaForm.imagenUrl}
                  onChange={(e) =>
                    setPreguntaForm({
                      ...preguntaForm,
                      imagenUrl: e.target.value,
                    })
                  }
                  placeholder="URL de la imagen"
                />
              </div>
              <div>
                <Label htmlFor="pregunta-orden">Orden</Label>
                <Input
                  id="pregunta-orden"
                  type="number"
                  value={preguntaForm.orden}
                  onChange={(e) =>
                    setPreguntaForm({
                      ...preguntaForm,
                      orden: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPreguntaModal(false);
                    setPreguntaForm({
                      contenidoId: 0,
                      tipoPregunta: "SELECCION_MULTIPLE",
                      pregunta: "",
                      opciones: [
                        { valor: "a", texto: "", esCorrecta: false },
                        { valor: "b", texto: "", esCorrecta: false },
                        { valor: "c", texto: "", esCorrecta: false },
                        { valor: "d", texto: "", esCorrecta: false },
                      ],
                      respuestaCorrecta: "",
                      explicacion: "",
                      imagenUrl: "",
                      orden: 1,
                    });
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={editingPregunta ? handleUpdatePregunta : handleCreatePregunta}>
                  {editingPregunta ? "Actualizar Pregunta" : "Crear Pregunta"}
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Confirmar Eliminar Mundo */}
        {deleteMundoConfirm && (
          <Modal
            isOpen={!!deleteMundoConfirm}
            onClose={() => setDeleteMundoConfirm(null)}
            title="Confirmar Eliminación"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                ¿Estás seguro de que deseas eliminar el mundo{" "}
                <strong>{deleteMundoConfirm.nombre}</strong>?
              </p>
              <p className="text-sm text-red-600">
                Esta acción no se puede deshacer. Se eliminarán todos los módulos, contenidos y preguntas asociados.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteMundoConfirm(null)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeleteMundo}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Confirmar Eliminar Módulo */}
        {deleteModuloConfirm && (
          <Modal
            isOpen={!!deleteModuloConfirm}
            onClose={() => setDeleteModuloConfirm(null)}
            title="Confirmar Eliminación"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                ¿Estás seguro de que deseas eliminar el módulo{" "}
                <strong>{deleteModuloConfirm.nombre}</strong>?
              </p>
              <p className="text-sm text-red-600">
                Esta acción no se puede deshacer. Se eliminarán todos los contenidos y preguntas asociados.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteModuloConfirm(null)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeleteModulo}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Confirmar Eliminar Contenido */}
        {deleteContenidoConfirm && (
          <Modal
            isOpen={!!deleteContenidoConfirm}
            onClose={() => setDeleteContenidoConfirm(null)}
            title="Confirmar Eliminación"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                ¿Estás seguro de que deseas eliminar el contenido{" "}
                <strong>{deleteContenidoConfirm.titulo || deleteContenidoConfirm.tipoContenido}</strong>?
              </p>
              <p className="text-sm text-red-600">
                Esta acción no se puede deshacer. Se eliminarán todas las preguntas asociadas.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteContenidoConfirm(null)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeleteContenido}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Modal Confirmar Eliminar Pregunta */}
        {deletePreguntaConfirm && (
          <Modal
            isOpen={!!deletePreguntaConfirm}
            onClose={() => setDeletePreguntaConfirm(null)}
            title="Confirmar Eliminación"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                ¿Estás seguro de que deseas eliminar la pregunta{" "}
                <strong>"{deletePreguntaConfirm.pregunta.substring(0, 50)}{deletePreguntaConfirm.pregunta.length > 50 ? '...' : ''}"</strong>?
              </p>
              <p className="text-sm text-red-600">
                Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeletePreguntaConfirm(null)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeletePregunta}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Notificaciones */}
        {notification && (
          <Notification
            isOpen={notification.isOpen}
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default EjerciciosEducativosPage;
